import React, { useState, useEffect } from "react";
import { MapPin, Building2, Calendar, Clock , Users, Store } from "lucide-react";

import "./AdvertisingSlots.css";

import BookingModal from "./AdvertisingModal";
import PaymentOptionModal from "../pyment/PaymentOption";
import PaymentQRModal from "../pyment/PaymentQRModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchStates, fetchDistricts } from "../../../../features/master/masterThunks";
import {fetchAdvtDetails, bookAdvtSlot,} from "../../../../features/advertisement/advertisementThunks";
import { clearSlots } from "../../../../features/advertisement/advertisementSlice";
import { clearCashPayment } from "../../../../features/payment/paymentSlice";
import useOnlinePayment from "../../../../hooks/useOnlinePayment";
import { createCashPayment, checkCashPaymentStatus  } from "../../../../features/payment/paymentThunks";
import PaymentSummaryModal from "../pyment/PymentSummary";


/* -------------------- HELPERS -------------------- */
const formatDateForAPI = (date) => {
  const d = new Date(date);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const getMinDate = () => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return t.toISOString().split("T")[0];
};

/* -------------------- COMPONENT -------------------- */
const AdvertisingSlots = () => {
  const dispatch = useDispatch();

  /* ---------------- REDUX ---------------- */
  const { states, districts } = useSelector((state) => state.master);
  const { advt, slots, loading, bookingLoading } = useSelector(
    (state) => state.advertisement
  );
  const { cardImage, cardType } = useSelector((state) => state.advtCard);


  /* ---------------- LOCAL STATE ---------------- */
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
const [pendingBooking, setPendingBooking] = useState(null);
  const { startOnlinePayment } = useOnlinePayment();
const [paymentSuccessful, setPaymentSuccessful] = useState(false);
const { cashPaymentData, status, payment_status } = useSelector(
  (state) => state.payment
);


useEffect(() => {
  // Start polling ONLY when QR is created and payment not successful
  if (!cashPaymentData?.orderId || paymentSuccessful) return;

  const interval = setInterval(() => {
    dispatch(checkCashPaymentStatus(cashPaymentData.orderId));
  }, 3000); // every 3 seconds

  return () => clearInterval(interval);
}, [cashPaymentData?.orderId, paymentSuccessful, dispatch]);


useEffect(() => {
  if (payment_status === "paid" && !paymentSuccessful) {
    setPaymentSuccessful(true);

    setTimeout(() => {
      finalizeBooking(); // ✅ BOOK AFTER PAYMENT
    }, 1500);
  }
}, [payment_status]);

  /* ---------------- LOAD STATES ---------------- */
  useEffect(() => {
    dispatch(fetchStates("68e8ef76a65b0a7d7ec75a29"));
  }, [dispatch]);

  /* ---------------- LOAD DISTRICTS ---------------- */
  useEffect(() => {
    if (selectedState) {
      dispatch(fetchDistricts(selectedState));
    }
  }, [dispatch, selectedState]);

  /* ---------------- FETCH ADVERTISEMENT ---------------- */
  useEffect(() => {
    if (selectedState && selectedDistrict && selectedDate) {
      dispatch(
        fetchAdvtDetails({
          stateId: selectedState,
          districtId: selectedDistrict,
          year: new Date(selectedDate).getFullYear(),
          month: new Date(selectedDate).toLocaleString("en-US", {
            month: "long",
          }),
          date: formatDateForAPI(selectedDate),
        })
      );
    }
  }, [dispatch, selectedState, selectedDistrict, selectedDate]);

  /* ---------------- PAYMENT HANDLER ---------------- */
const handlePaymentSelect = async (method) => {
  if (!bookingData?.bookingId) return;

  // 🔵 CASH
  if (method === "cash") {
    await dispatch(
      createCashPayment({
        bookingId: bookingData.bookingId,
        amount: bookingData.totalAmount,
        paymentFor: "advertisement",
      })
    );
    setPaymentOpen(false);
  }

  // 🟢 ONLINE
  if (method === "online") {
    setPaymentOpen(false);

    startOnlinePayment({
      bookingId: bookingData.bookingId,
      amount: bookingData.totalAmount,
      paymentFor: "advertisement",
      onSuccess: () => window.location.reload(),
      onFailure: console.error,
    });
  }
};




  /* ---------------- BOOK SLOT ---------------- */
const handleConfirmBooking = async () => {
  if (!advt?.id || !selectedSlot || !selectedDate) return;

  const res = await dispatch(
    bookAdvtSlot({
      advtId: advt.id,
      slotNumber: selectedSlot.slot,
      date: formatDateForAPI(selectedDate),
      cardImage,
    })
  );

  if (bookAdvtSlot.fulfilled.match(res)) {
    setBookingData(res.payload.data); // ✅ BACKEND DATA
    setModalOpen(false);
    setSummaryOpen(true); // ✅ open summary AFTER booking
  }
};



const finalizeBooking = async () => {
  if (!pendingBooking) return;

  await dispatch(
    bookAdvtSlot({
      advtId: advt.id,
      slotNumber: pendingBooking.slot.slot,
      date: formatDateForAPI(pendingBooking.date),
      cardImage,
    })
  );

  window.location.reload();
};


const handleClosePayment = () => {
  // 1️⃣ CLOSE MODAL (this is what actually closes it)
  dispatch(clearCashPayment());

  // 2️⃣ RESET LOCAL FLAGS
  setPaymentSuccessful(false);

  // 3️⃣ REFRESH DATA
  if (selectedState && selectedDistrict && selectedDate) {
    dispatch(
      fetchAdvtDetails({
        stateId: selectedState,
        districtId: selectedDistrict,
        year: new Date(selectedDate).getFullYear(),
        month: new Date(selectedDate).toLocaleString("en-US", {
          month: "long",
        }),
        date: formatDateForAPI(selectedDate),
      })
    );
  }
};



  /* ---------------- RENDER ---------------- */
  return (
    <div className="advertise-container">
      <h3 className="fw-bold mb-3">Advertise - {cardType}</h3>

      <div className="advertise-card mb-3 shadow-sm">
        <h5 className="fw-bold">{cardType}</h5>
        <p className="text-muted">Boost your visibility with a premium slot</p>
      </div>

      {/* ---------------- FILTERS ---------------- */}
      <div className="details-box shadow-sm">
        <h5 className="fw-bold mb-3">Location Details</h5>

        <div className="row g-3">
          {/* State */}
          <div className="col-md-4">
            <label className="form-label">State</label>
            <div className="dropdown-box">
              <MapPin className="drop-icon" />
              <select
                className="form-select drop-select"
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  setSelectedDistrict("");
                  setSelectedDate("");
                  dispatch(clearSlots());
                }}
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* District */}
          <div className="col-md-4">
            <label className="form-label">District</label>
            <div className="dropdown-box">
              <Building2 className="drop-icon" />
              <select
                className="form-select drop-select"
                value={selectedDistrict}
                onChange={(e) => {
                  setSelectedDistrict(e.target.value);
                  dispatch(clearSlots());
                }}
              >
                <option value="">Select District</option>
                {districts.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date */}
          <div className="col-md-4">
            <label className="form-label">Select Date</label>
            <div className="dropdown-box">
              <Calendar className="drop-icon" />
              <input
                type="date"
                min={getMinDate()}
                className="form-control drop-select"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  dispatch(clearSlots());
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- ADVERTISEMENT REACH ---------------- */}
{/* ---------------- ADVERTISEMENT REACH ---------------- */}
{advt && (
  <div className="reach-card">
    <div className="reach-header">
      <div className="reach-icon">
        <Users size={18} />
      </div>
      <h5>Your Advertisement Reach</h5>
    </div>

    <div className="reach-stats">
      <div className="reach-box">
        <Users size={26} className="reach-stat-icon" />
        <span className="reach-number">
          {advt.total_no_of_users}
        </span>
        <span className="reach-label">Users</span>
      </div>

      <div className="reach-box">
        <Store size={26} className="reach-stat-icon" />
        <span className="reach-number">
          {advt.total_no_of_merchants}
        </span>
        <span className="reach-label">Merchants</span>
      </div>
    </div>

    <div className="reach-info">
      Your advertisement will reach these people in the selected area
    </div>
  </div>
)}



      {/* ---------------- SLOTS ---------------- */}
      <h5 className="fw-bold mt-4">Available Time Slots</h5>

      {loading ? (
        <div className="loading-box">Loading advertisement...</div>
      ) : !advt ? (
        <div className="no-slot-box">
          <p>Please select state, district and date</p>
        </div>
      ) : slots.length === 0 ? (
        <div className="no-slot-box">
          <Clock size={40} />
          <p>No slots found</p>
        </div>
      ) : (
        <div className="slot-grid">
          {slots.map((slot, idx) => (
            <div
              key={idx}
              className={`slot-card ${
                slot.slotStatus !== "available" ? "booked" : ""
              }`}
              onClick={() => {
                if (slot.slotStatus !== "available") return;
                setSelectedSlot(slot);
                setModalOpen(true);
              }}
            >
              <p>{slot.start_time} - {slot.end_time}</p>
              <p>₹{slot.price}</p>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- MODALS ---------------- */}
      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        slot={selectedSlot}
        date={selectedDate}
        cardImage={cardImage}
        loading={bookingLoading}
        onConfirm={handleConfirmBooking}
      />

    <PaymentSummaryModal
  show={summaryOpen}
  slot={{
    amount: bookingData?.price,
    gstPercentage: bookingData?.gstPercentage,
    gstAmount: bookingData?.gstAmount,
    totalAmount: bookingData?.totalAmount,
  }}
  onClose={() => setSummaryOpen(false)}
  onBookClick={() => {
    setSummaryOpen(false);
    setPaymentOpen(true);
  }}
/>




      <PaymentOptionModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        onSelect={handlePaymentSelect}
      />

      <PaymentQRModal
  open={Boolean(cashPaymentData)}
  data={cashPaymentData}
  status={status}
  payment_status={payment_status}
 onClose={handleClosePayment}
/>
    </div>
  );
};

export default AdvertisingSlots;
